/**
 * Uniqueness for a select attribute's value set (DV-468).
 *
 * Deliberately stricter than the backend, which compares exactly ("option values
 * must be unique") and only fires after submit: "High" and "high" pass there but
 * read as one value to a person. Shared so the two screens that define value sets
 * — `DocumentTypeSettings` and `SpaceEditor` — can't drift apart.
 */

import type { AttrDataType } from "#common/types/vault";

export const normalizeAttributeValue = (value: string): string => value.trim().toLowerCase();

/** Indexes that share a value with another entry; blanks are never duplicates. */
export function findDuplicateValueIndexes(values: readonly string[]): Set<number> {
  const indexesByValue = new Map<string, number[]>();
  values.forEach((value, index) => {
    const key = normalizeAttributeValue(value);
    if (!key) return;
    indexesByValue.set(key, [...(indexesByValue.get(key) ?? []), index]);
  });
  const duplicates = new Set<number>();
  indexesByValue.forEach((indexes) => {
    if (indexes.length > 1) indexes.forEach((index) => duplicates.add(index));
  });
  return duplicates;
}

export function isDuplicateAttributeValue(
  candidate: string,
  values: readonly string[],
): boolean {
  const key = normalizeAttributeValue(candidate);
  if (!key) return false;
  return values.some((value) => normalizeAttributeValue(value) === key);
}

export const DUPLICATE_VALUE_MESSAGE =
  "Duplicate values aren’t allowed — each value must be unique.";

/**
 * Why an already-saved entry in an attribute's value set is read-only (DV-674).
 * Documents store a picked select value as the option's ID, so renaming a saved
 * value in place would silently relabel it for every document already carrying
 * it, and dropping one would break those references — the backend refuses that
 * removal outright (`AttributeOptionInUseError`). Adding values stays open.
 */
export const SAVED_VALUE_SET_LOCK_MESSAGE =
  "Saved values can't be renamed or removed — documents already filed reference them. " +
  "You can add new values.";

/**
 * The only shape the backend `Date` scalar accepts (DV-563). Date pickers convert
 * to this on commit; leftover free-text paths still advertise it so a failed
 * submit isn't how the format is discovered.
 */
export const DATE_FORMAT_HINT = "YYYY-MM-DD";

/** Display placeholder on the design-system DatePicker (`dateFormat` MM/dd/yyyy). */
export const DATE_PICKER_PLACEHOLDER = "MM/DD/YYYY";

/** Tooltip copy for a date cell that has no placeholder to carry the hint. */
export const DATE_FORMAT_TOOLTIP = `Enter the date as ${DATE_FORMAT_HINT}`;

/**
 * Tooltip copy for a date cell that already holds a value (DV-705).
 *
 * A date has no empty form the API can persist, so emptying the picker is not an
 * edit the grid accepts — the only way out of a date is another date. The cell
 * says so up front rather than letting the user discover it from a value that
 * snaps back.
 */
export const DATE_REPLACE_TOOLTIP = `Pick a replacement date (${DATE_FORMAT_HINT}) — a date can’t be cleared`;

/* Tri-state blank-cell copy (DV-504): an attribute cell with no value is either
   bound-but-empty ("—") or not bound to the document's space at all ("N/A"). */

/** Placeholder for an attribute that applies but holds no value. */
export const EMPTY_DISPLAY = "—";

/** Placeholder for an attribute that is not bound to the document's space. */
export const NOT_APPLICABLE_DISPLAY = "N/A";

/** Tooltip on an "—" cell. */
export const NO_VALUE_TOOLTIP = "No value set";

/** Tooltip on an "N/A" cell. */
export const NOT_APPLICABLE_TOOLTIP =
  "Not applicable — this attribute is not used in this document's space";

/** Date-typed value inputs advertise the picker format; other types keep their own copy. */
export function attributeValuePlaceholder(dataType: AttrDataType, fallback: string): string {
  return dataType === "Date" ? DATE_PICKER_PLACEHOLDER : fallback;
}

/** Parse a calendar day without timezone shift (`YYYY-MM-DD`, optional trailing time). */
export function parseDateOnly(value: string | null | undefined): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  // `new Date(y, m, d)` remaps y in 0–99 to 1900–1999. `fromisoformat` accepts
  // years 0001–0099, so setFullYear after construct (same trap as Date.UTC).
  if (year < 1 || month < 1 || month > 12 || day < 1) return null;
  const date = new Date(year, month - 1, day);
  date.setFullYear(year);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

/** Format a Date as the backend `Date` scalar (`YYYY-MM-DD`) from local Y/M/D. */
export function formatDateOnly(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
